import React, { useState } from "react";
import {
  Container,
  Grid,
  Button,
  TextField,
  Typography,
  Paper,
  Box,
  Stack,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CallIcon from "@mui/icons-material/Call";
import EmailIcon from "@mui/icons-material/Email";
import InstagramIcon from "@mui/icons-material/Instagram";
import emailjs from "emailjs-com";
import { colors, fonts, ui } from "../theme";

const blueprint = [
  { step: "1", title: "Discovery Consultation", text: "Schedule an initial diagnostic review for your departments." },
  { step: "2", title: "Ecosystem Mapping", text: "We work with your IQAC and department heads to analyse curriculum gaps and accreditation goals." },
  { step: "3", title: "Strategic Implementation", text: "We establish a structured MoU tailored to your institution's requirements." },
];

const Contact = () => {
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", message: "" });
  const [errors, setErrors] = useState({ name: "", email: "", phone: "", message: "" });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMessage, setDialogMessage] = useState("");

  const validate = () => {
    let tempErrors = { name: "", email: "", phone: "", message: "" };
    let isValid = true;

    if (!formData.name) {
      tempErrors.name = "Name is required.";
      isValid = false;
    }
    if (!formData.email) {
      tempErrors.email = "Email is required.";
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      tempErrors.email = "Email is not valid.";
      isValid = false;
    }
    if (!formData.phone) {
      tempErrors.phone = "Phone number is required.";
      isValid = false;
    } else if (!/^\d{10}$/.test(formData.phone)) {
      tempErrors.phone = "Phone number is not valid.";
      isValid = false;
    }
    if (!formData.message) {
      tempErrors.message = "Message is required.";
      isValid = false;
    }

    setErrors(tempErrors);
    return isValid;
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      emailjs
        .send("service_0gnaieq", "template_pfopqkc", formData, "edCtLG7Df-rPBgo1n")
        .then(
          () => {
            setDialogMessage("Thank you — your enquiry has been sent successfully. Our engagement team will be in touch shortly.");
            setDialogOpen(true);
          },
          (error) => {
            console.error("Error sending email:", error);
            setDialogMessage("Failed to send your enquiry. Please try again later.");
            setDialogOpen(true);
          }
        );
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    if (dialogMessage.startsWith("Thank you")) {
      window.location.assign("/");
    }
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": { backgroundColor: colors.white },
  };

  // Clickable contact rows — subtle slide + colour shift on hover.
  const contactLinkSx = {
    color: "inherit",
    textDecoration: "none",
    width: "fit-content",
    borderRadius: "8px",
    transition: "transform .2s ease, color .2s ease",
    "&:hover": { color: colors.blue, transform: "translateX(4px)" },
  };

  return (
    <section id="contacts-section" className="contacts-section">
      <Box sx={{ background: colors.navy, py: { xs: 5, md: 7 } }}>
        <Container>
          <Grid container spacing={{ xs: 4, md: 5 }} alignItems="stretch">
            {/* Left — invitation + blueprint + details */}
            <Grid item xs={12} md={6}>
              <Typography sx={{ color: colors.blue, fontWeight: 700, letterSpacing: "0.1em", fontSize: "0.8rem", mb: 1.5 }}>
                PARTNERSHIP INVITATION
              </Typography>
              <Typography
                variant="h4"
                sx={{ fontFamily: fonts.heading, fontWeight: 700, color: colors.white, mb: 2, fontSize: { xs: "1.6rem", md: "2rem" } }}
              >
                Let's Build Your Institution's Innovation Ecosystem
              </Typography>
              <Typography sx={{ color: "rgba(255,255,255,0.8)", lineHeight: 1.7, mb: 4 }}>
                Move beyond seasonal vendor engagements. Partner with Crescent EdTech to modernise your
                departments, empower your faculty and prepare students to lead in the modern technology landscape.
              </Typography>

              <Stack spacing={2.5} sx={{ mb: 4 }}>
                {blueprint.map((b) => (
                  <Stack direction="row" spacing={2} key={b.step} alignItems="flex-start">
                    <Box
                      sx={{
                        minWidth: 36,
                        height: 36,
                        borderRadius: "50%",
                        background: colors.brandGradient,
                        color: colors.white,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontFamily: fonts.heading,
                        fontWeight: 700,
                      }}
                    >
                      {b.step}
                    </Box>
                    <Box>
                      <Typography sx={{ color: colors.white, fontWeight: 700, fontFamily: fonts.heading, fontSize: "0.98rem" }}>
                        {b.title}
                      </Typography>
                      <Typography sx={{ color: "rgba(255,255,255,0.72)", fontSize: "0.88rem", lineHeight: 1.55 }}>
                        {b.text}
                      </Typography>
                    </Box>
                  </Stack>
                ))}
              </Stack>

              <Stack spacing={1.5} sx={{ color: "rgba(255,255,255,0.9)" }}>
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                  <LocationOnIcon sx={{ color: colors.blue, fontSize: 20, mt: "2px" }} />
                  <Typography sx={{ fontSize: "0.9rem" }}>
                    196/8, Gokulakrishnan Street, AVK Nagar, Opp. New Bus Stand, Salem – 636004, Tamil Nadu, India
                  </Typography>
                </Stack>
                <Stack
                  component="a"
                  href="tel:+918667002959"
                  direction="row"
                  spacing={1.5}
                  alignItems="center"
                  sx={contactLinkSx}
                >
                  <CallIcon sx={{ color: colors.blue, fontSize: 20 }} />
                  <Typography sx={{ fontSize: "0.9rem" }}>+91 86670 02959</Typography>
                </Stack>
                <Stack
                  component="a"
                  href="mailto:reachus@crescentedtech.in"
                  direction="row"
                  spacing={1.5}
                  alignItems="center"
                  sx={contactLinkSx}
                >
                  <EmailIcon sx={{ color: colors.blue, fontSize: 20 }} />
                  <Typography sx={{ fontSize: "0.9rem" }}>reachus@crescentedtech.in</Typography>
                </Stack>
                <Stack
                  component="a"
                  href="https://www.instagram.com/crescentedtech/"
                  target="_blank"
                  rel="noopener"
                  direction="row"
                  spacing={1.5}
                  alignItems="center"
                  sx={contactLinkSx}
                >
                  <InstagramIcon sx={{ color: colors.blue, fontSize: 20 }} />
                  <Typography sx={{ fontSize: "0.9rem" }}>@crescentedtech</Typography>
                </Stack>
              </Stack>
            </Grid>

            {/* Right — form */}
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: { xs: 3, md: 4 }, borderRadius: 4, bgcolor: colors.white }}>
                <Typography variant="h5" sx={{ fontFamily: fonts.heading, fontWeight: 700, color: colors.navy, mb: 0.5 }}>
                  Request a Discovery Consultation
                </Typography>
                <Typography sx={{ color: colors.textMuted, mb: 2.5, fontSize: "0.92rem" }}>
                  Tell us about your institution and we'll get back to you.
                </Typography>
                <form onSubmit={handleSubmit}>
                  <TextField
                    fullWidth
                    label="Your Name / Designation"
                    name="name"
                    onChange={handleChange}
                    value={formData.name}
                    variant="outlined"
                    margin="normal"
                    required
                    sx={inputSx}
                    error={!!errors.name}
                    helperText={errors.name}
                  />
                  <TextField
                    fullWidth
                    label="Email Address"
                    name="email"
                    onChange={handleChange}
                    value={formData.email}
                    variant="outlined"
                    margin="normal"
                    type="email"
                    required
                    sx={inputSx}
                    error={!!errors.email}
                    helperText={errors.email}
                  />
                  <TextField
                    fullWidth
                    label="Phone"
                    name="phone"
                    onChange={handleChange}
                    value={formData.phone}
                    variant="outlined"
                    margin="normal"
                    type="tel"
                    required
                    sx={inputSx}
                    error={!!errors.phone}
                    helperText={errors.phone}
                  />
                  <TextField
                    fullWidth
                    label="Institution & Requirement"
                    name="message"
                    onChange={handleChange}
                    value={formData.message}
                    variant="outlined"
                    margin="normal"
                    multiline
                    rows={4}
                    required
                    sx={inputSx}
                    error={!!errors.message}
                    helperText={errors.message}
                  />
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    disableElevation
                    sx={{ ...ui.ctaButton, mt: 2, py: 1.2 }}
                  >
                    Submit Enquiry
                  </Button>
                </form>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Dialog open={dialogOpen} onClose={handleDialogClose} aria-labelledby="alert-dialog-title">
        <DialogTitle id="alert-dialog-title">Partnership Enquiry</DialogTitle>
        <DialogContent>
          <DialogContentText>{dialogMessage}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} autoFocus>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </section>
  );
};

export default Contact;
